import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PlumbingIcon from '@mui/icons-material/Plumbing';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import SourceIcon from '@mui/icons-material/Source';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import React, { Suspense, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import EntityConfiguration from '@/components/entities/configuration';
import ErrorMessage from '@/components/errorMessage';
import PipelineBuilder from '@/components/pipelineBuilder/pipelineBuilder';
import TabPanel from '@/components/tabPanel';
import { useApiContext } from '@/contexts/apiContext';
import { useUserContext } from '@/contexts/userContext';
import { useElementSize } from '@custom-react-hooks/use-element-size';
import { useTranslation } from 'react-i18next';
import UserAppInstanceAdministration from './administration';
import DataSources from './dataSources';
import UserAppHeader from './header';
import ViewUserAppItem from './viewUserAppItem';

import { resolveCanvases, ResolvedCanvas } from './widgets/resolveCanvases';

/**
 * UserAppShell — the single unified User App component that replaces all
 * domain-specific app components (generalQnAApp, telemetryDashboardApp,
 * maintenanceConsoleApp, etc.).
 *
 * It resolves canvas widgets dynamically from the sourceAgentDefs declared on
 * the user_app_def, using the agent def metadata.ui.canvasTypes to look up
 * the widget registry.
 *
 * Common tabs (DataSources, Configuration, Pipeline, Administration) are
 * always rendered. Canvas-specific tabs are resolved at runtime.
 */

type Props = {
  userAppInstance: any;
  updateUserAppInstance: (updatedApp: any) => void;
  reloadUserAppInstance: () => void;
  error: any;
};

const UserAppShell = ({ userAppInstance, updateUserAppInstance, reloadUserAppInstance, error }: Props) => {
  const { t } = useTranslation();
  const { hasPermission } = useUserContext();
  const navigate = useNavigate();
  const location = useLocation();
  const apiContext = useApiContext();

  const [containerRef, containerSize] = useElementSize();
  const [topPanelRef, topPanelSize] = useElementSize();

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTab, setSelectedTab] = useState<string>('');
  const [optionTabs, setOptionTabs] = useState<any[]>([]);
  const [openItems, setOpenItems] = useState<any[]>([]);

  // Canvas resolution state
  const [resolvedCanvases, setResolvedCanvases] = useState<ResolvedCanvas[]>([]);

  // Source events (loaded once for all canvas widgets)
  const [sourceEvents, setSourceEvents] = useState<any[]>([]);
  const [loadingEvents, setLoadingEvents] = useState<boolean>(false);

  const menuOpen = Boolean(menuAnchorEl);
  const maxContentHeight = containerSize.height - topPanelSize.height - 4;

  // ──────────────────────────────────────────────────────────────────────
  // 1. Resolve canvases from sourceAgentDefs + agentDef metadata
  // ──────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const resolveFromDef = async () => {
      const appDef = userAppInstance?.userAppDef;
      const sourceAgentDefs: string[] = appDef?.sourceAgentDefs || [];
      const appDefId = userAppInstance?.userAppDefId || userAppInstance?.entityDefId;

      if (sourceAgentDefs.length === 0) {
        // No sourceAgentDefs — use legacy fallback
        const canvases = resolveCanvases([], {}, appDefId);
        setResolvedCanvases(canvases);
        if (canvases.length > 0) {
          setSelectedTab(canvases[0].widget.canvasType);
        } else {
          setSelectedTab('dataSources');
        }
        return;
      }

      try {
        // Fetch agent def metadata for all sourceAgentDefs
        // For now, we try to get agent defs one by one and extract metadata
        const metadataMap: Record<string, { canvasTypes?: string[] }> = {};

        await Promise.all(
          sourceAgentDefs.map(async (agentDefId: string) => {
            try {
              const agentDef = await apiContext.getAgentDef(agentDefId);
              if (agentDef?.metadata?.ui) {
                metadataMap[agentDefId] = agentDef.metadata.ui;
              }
            } catch (err) {
              // Agent def not found or no metadata — skip
              console.warn(`Could not fetch metadata for agent def ${agentDefId}`, err);
            }
          }),
        );

        const canvases = resolveCanvases(sourceAgentDefs, metadataMap, appDefId);
        setResolvedCanvases(canvases);

        if (canvases.length > 0) {
          setSelectedTab(canvases[0].widget.canvasType);
        } else {
          setSelectedTab('dataSources');
        }
      } catch (err) {
        console.error('Error resolving canvases', err);
        const canvases = resolveCanvases([], {}, appDefId);
        setResolvedCanvases(canvases);
        if (canvases.length > 0) {
          setSelectedTab(canvases[0].widget.canvasType);
        }
      }
    };

    if (userAppInstance) {
      resolveFromDef();
    }
  }, [userAppInstance?.id]);

  // ──────────────────────────────────────────────────────────────────────
  // 2. Fetch source events (shared across all canvas widgets)
  // ──────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchSourceEvents = async () => {
      try {
        setLoadingEvents(true);
        const statuses = await apiContext.getUserAppInstanceItemProcessingStatuses(
          userAppInstance.userAppDefId,
          userAppInstance.id,
        );

        const allEvents: any[] = [];
        await Promise.all(
          statuses.map(async (status: any) => {
            try {
              const events = await apiContext.getDataSourceItemEventsData(
                status.dataSourceDefId,
                status.dataSourceInstanceId,
                status.dataSourceItemId,
              );
              allEvents.push(...events);
            } catch (err) {
              console.error('Error fetching events for status', status, err);
            }
          }),
        );

        setSourceEvents(allEvents);
      } catch (err) {
        console.error('Error fetching source events', err);
      } finally {
        setLoadingEvents(false);
      }
    };

    if (userAppInstance) {
      fetchSourceEvents();
    }
  }, [userAppInstance?.id]);

  // ──────────────────────────────────────────────────────────────────────
  // Tab management (shared boilerplate — extracted from all app components)
  // ──────────────────────────────────────────────────────────────────────
  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
  };

  const getTabId = (item: any) => {
    const appSourceEntities = userAppInstance?.sourceEntities || [];
    const isItemFromAppSource = appSourceEntities.some(
      (se: any) =>
        se.sourceEntityId?.toString() === item?.dataSourceInstanceId?.toString() &&
        (se.sourceEntityType === 'dataSourceInstance' || se.sourceEntityType === 'dataSourceDef'),
    );

    if (item.dataSourceItemGroup && isItemFromAppSource) {
      return `group-${item.dataSourceDefId}-${item.dataSourceInstanceId}-${item.dataSourceItemGroup}`;
    }
    const itemId = item.dataSourceItemId || item.id;
    return `item-${item.dataSourceDefId}-${item.dataSourceInstanceId}-${itemId}`;
  };

  const onOpenItem = (item: any) => {
    const tabId = getTabId(item);
    const existing = openItems.find((open) => getTabId(open) === tabId);

    if (existing) {
      setSelectedTab(tabId);
    } else {
      const newItem = { ...item, tabId };
      setOpenItems([...openItems, newItem]);
      setSelectedTab(tabId);
    }
  };

  const addOptionsTab = (option: any) => {
    const o = optionTabs.find((o) => o.value === option.value);
    if (o) {
      setSelectedTab(o.value);
    } else {
      setOptionTabs([...optionTabs, option]);
      setSelectedTab(option.value);
    }
    closeMenu();
  };

  const defaultTab = resolvedCanvases.length > 0 ? resolvedCanvases[0].widget.canvasType : 'dataSources';

  const deleteItemTab = (tabId: string) => {
    setOpenItems(openItems.filter((i) => getTabId(i) !== tabId));
    if (selectedTab === tabId) {
      setSelectedTab(defaultTab);
    }
  };

  const deleteOptionTab = (value: string) => {
    setOptionTabs(optionTabs.filter((o) => o.value !== value));
    if (selectedTab === value) {
      setSelectedTab(defaultTab);
    }
  };

  const openOptionsMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    setMenuAnchorEl(null);
  };

  const close = () => {
    const from = location.state?.from || '/instances?tab=userAppInstances';
    navigate(from);
  };

  // ──────────────────────────────────────────────────────────────────────
  // Shared tab styling
  // ──────────────────────────────────────────────────────────────────────
  const tabSx = (tabValue: string) => ({
    minHeight: '40px',
    px: 2,
    transition: 'all 0.2s',
    opacity: selectedTab === tabValue ? 1 : 0.7,
    '&:hover': { opacity: 1, bgcolor: 'rgba(139, 216, 188, 0.05)' },
  });

  const tabLabelSx = (tabValue: string) => ({
    fontWeight: selectedTab === tabValue ? '800' : '500',
    color: selectedTab === tabValue ? 'primary.main' : 'text.secondary',
  });

  // ──────────────────────────────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────────────────────────────
  return (
    <Stack
      sx={{
        height: '100%',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
      ref={containerRef}
    >
      {/* ─── Header ─── */}
      <Stack ref={topPanelRef} sx={{ zIndex: 10 }}>
        <UserAppHeader userAppInstance={userAppInstance} onClose={close} />
        {error && <ErrorMessage error={error} />}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems="stretch"
          className="glass-card-light"
          sx={{
            mx: 1,
            my: 0.5,
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: 'none',
            overflow: 'hidden',
            pb: { xs: 1, sm: 0 },
          }}
        >
          <Box sx={{ maxWidth: { xs: '100%', sm: '70vw' }, width: '100%', pl: 1 }}>
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              indicatorColor="secondary"
              variant="scrollable"
              sx={{
                minHeight: '40px',
                '& .MuiTabs-indicator': {
                  height: '3px',
                  borderRadius: '3px 3px 0 0',
                  bgcolor: '#8BD8BC',
                },
              }}
            >
              {/* ─── Dynamic Canvas Tabs ─── */}
              {resolvedCanvases.map((canvas) => (
                <Tab
                  key={canvas.widget.canvasType}
                  value={canvas.widget.canvasType}
                  iconPosition="start"
                  sx={tabSx(canvas.widget.canvasType)}
                  label={
                    <Typography
                      variant={selectedTab === canvas.widget.canvasType ? 'subtitle2' : 'caption'}
                      sx={tabLabelSx(canvas.widget.canvasType)}
                    >
                      {canvas.widget.label}
                    </Typography>
                  }
                />
              ))}

              {/* ─── Data Sources Tab ─── */}
              {hasPermission(`pipeline:apps:dataSources:view`, userAppInstance?.userAccess) && (
                <Tab
                  value={'dataSources'}
                  icon={<SourceIcon fontSize="small" />}
                  iconPosition="start"
                  sx={tabSx('dataSources')}
                  label={
                    <Typography
                      variant={selectedTab === 'dataSources' ? 'subtitle2' : 'caption'}
                      sx={tabLabelSx('dataSources')}
                    >
                      Data Sources
                    </Typography>
                  }
                />
              )}

              {/* ─── Configuration Tab ─── */}
              {hasPermission(`pipeline:apps:configuration:view`, userAppInstance?.userAccess) && (
                <Tab
                  value={'configuration'}
                  icon={<SettingsOutlinedIcon fontSize="small" />}
                  iconPosition="start"
                  sx={tabSx('configuration')}
                  label={
                    <Typography
                      variant={selectedTab === 'configuration' ? 'subtitle2' : 'caption'}
                      sx={tabLabelSx('configuration')}
                    >
                      Configuration
                    </Typography>
                  }
                />
              )}

              {/* ─── Option Tabs (Pipeline, Administration) ─── */}
              {optionTabs.map((option) => (
                <Tab
                  key={option.value}
                  value={option.value}
                  icon={option.icon}
                  iconPosition="start"
                  sx={{
                    ...tabSx(option.value),
                    px: 3,
                    position: 'relative',
                    overflow: 'visible',
                  }}
                  label={
                    <Box
                      component="span"
                      sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', pr: 1.5 }}
                    >
                      <Typography
                        title={option.label}
                        variant={selectedTab === option.value ? 'subtitle2' : 'caption'}
                        sx={{ fontWeight: selectedTab === option.value ? 'bold' : 'normal', maxWidth: '160px' }}
                        noWrap
                      >
                        {option.label}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          deleteOptionTab(option.value);
                        }}
                        sx={{
                          position: 'absolute',
                          top: -8,
                          right: -4,
                          p: 0.1,
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 10 }} />
                      </IconButton>
                    </Box>
                  }
                />
              ))}

              {/* ─── Dynamic Item Tabs ─── */}
              {openItems.map((item) => (
                <Tab
                  key={item.tabId}
                  value={item.tabId}
                  icon={<SourceIcon fontSize="small" />}
                  iconPosition="start"
                  sx={{
                    ...tabSx(item.tabId),
                    px: 3,
                    position: 'relative',
                    overflow: 'visible',
                  }}
                  label={
                    <Box
                      component="span"
                      sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', pr: 1.5 }}
                    >
                      <Typography
                        title={item.itemName ? item.itemName : `Group : ${item.dataSourceItemGroup}`}
                        variant={selectedTab === item.tabId ? 'subtitle2' : 'caption'}
                        sx={{ fontWeight: selectedTab === item.tabId ? 'bold' : 'normal', maxWidth: '120px' }}
                        noWrap
                      >
                        {item.itemName ? item.itemName : `Group : ${item.dataSourceItemGroup}`}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          deleteItemTab(item.tabId);
                        }}
                        sx={{
                          position: 'absolute',
                          top: -8,
                          right: -4,
                          p: 0.1,
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 10 }} />
                      </IconButton>
                    </Box>
                  }
                />
              ))}
            </Tabs>
          </Box>

          {/* ─── Options Button ─── */}
          <Stack
            direction={'row'}
            alignItems="center"
            justifyContent="flex-end"
            spacing={1.5}
            sx={{ px: 2, py: { xs: 1, sm: 0 }, pb: { xs: 1.5, sm: 0 } }}
          >
            <Button
              size="small"
              color="primary"
              variant="contained"
              onClick={openOptionsMenu}
              endIcon={<KeyboardArrowDownIcon />}
              sx={{
                borderRadius: '20px',
                px: 2,
                bgcolor: 'primary.main',
                fontWeight: 'bold',
                boxShadow: '0 4px 12px rgba(35, 53, 101, 0.2)',
                '&:hover': { bgcolor: 'primary.dark', boxShadow: '0 6px 16px rgba(35, 53, 101, 0.3)' },
              }}
            >
              {t('common.buttons.options')}
            </Button>
          </Stack>
        </Stack>
      </Stack>

      {/* ─── Tab Panels ─── */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'hidden',
          mx: 1,
          mb: 0.5,
          borderRadius: '12px',
          bgcolor: 'background.paper',
          boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
          position: 'relative',
        }}
      >
        {/* ─── Dynamic Canvas Tab Panels ─── */}
        {resolvedCanvases.map((canvas) => (
          <TabPanel
            key={canvas.widget.canvasType}
            value={canvas.widget.canvasType}
            currentTab={selectedTab}
            maxHeight={maxContentHeight}
          >
            <Suspense
              fallback={
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                  <CircularProgress />
                </Box>
              }
            >
              <canvas.widget.component
                userAppInstance={userAppInstance}
                sourceEvents={sourceEvents}
                loadingEvents={loadingEvents}
                maxHeight={maxContentHeight}
                updateUserAppInstance={updateUserAppInstance}
                onOpenItem={onOpenItem}
              />
            </Suspense>
          </TabPanel>
        ))}

        {/* ─── Data Sources Tab Panel ─── */}
        {hasPermission(`pipeline:apps:dataSources:view`, userAppInstance?.userAccess) && (
          <TabPanel value="dataSources" currentTab={selectedTab} maxHeight={maxContentHeight}>
            <DataSources
              userAppInstance={userAppInstance}
              maxHeight={maxContentHeight}
              onOpenDataSourceItem={onOpenItem}
            />
          </TabPanel>
        )}

        {/* ─── Configuration Tab Panel ─── */}
        {hasPermission(`pipeline:apps:configuration:view`, userAppInstance?.userAccess) && (
          <TabPanel value="configuration" currentTab={selectedTab} maxHeight={maxContentHeight}>
            <EntityConfiguration
              maxHeight={maxContentHeight}
              entity={userAppInstance}
              onSubmit={updateUserAppInstance}
            />
          </TabPanel>
        )}

        {/* ─── Pipeline Builder Tab Panel ─── */}
        {hasPermission(`pipeline:apps:configuration:view`, userAppInstance?.userAccess) && (
          <TabPanel value="pipelineBuilder" currentTab={selectedTab} maxHeight={maxContentHeight}>
            <PipelineBuilder
              maxHeight={maxContentHeight}
              entity={userAppInstance}
              isOpen={selectedTab === 'pipelineBuilder'}
              onConfigurationChange={reloadUserAppInstance}
            />
          </TabPanel>
        )}

        {/* ─── Administration Tab Panel ─── */}
        {(hasPermission(`pipeline:apps:accessControl:view`, userAppInstance?.userAccess) ||
          hasPermission(`pipeline:apps:usageStats:view`, userAppInstance?.userAccess) ||
          hasPermission(`pipeline:apps:auditTrail:view`, userAppInstance?.userAccess)) && (
          <TabPanel value="administration" currentTab={selectedTab} maxHeight={maxContentHeight}>
            <UserAppInstanceAdministration
              isOpen={optionTabs.some((o) => o.value === 'administration')}
              maxHeight={maxContentHeight}
              userAppInstance={userAppInstance}
            />
          </TabPanel>
        )}

        {/* ─── Dynamic Item Tab Panels ─── */}
        {openItems.map((item) => (
          <TabPanel key={item.tabId} value={item.tabId} currentTab={selectedTab} maxHeight={maxContentHeight}>
            <ViewUserAppItem userAppInstance={userAppInstance} item={item} maxHeight={maxContentHeight} />
          </TabPanel>
        ))}
      </Box>

      {/* ─── Options Menu ─── */}
      <Menu id="optionsMenu" anchorEl={menuAnchorEl} open={menuOpen} onClose={closeMenu}>
        {hasPermission(`pipeline:apps:configuration:view`, userAppInstance?.userAccess) && (
          <MenuItem
            onClick={() => addOptionsTab({ value: 'pipelineBuilder', label: 'Pipeline', icon: <PlumbingIcon /> })}
          >
            <ListItemIcon>
              <PlumbingIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Pipeline</ListItemText>
          </MenuItem>
        )}
        {(hasPermission(`pipeline:apps:accessControl:view`, userAppInstance?.userAccess) ||
          hasPermission(`pipeline:apps:usageStats:view`, userAppInstance?.userAccess) ||
          hasPermission(`pipeline:apps:auditTrail:view`, userAppInstance?.userAccess)) && (
          <MenuItem
            onClick={() =>
              addOptionsTab({
                value: 'administration',
                label: 'App administration',
                icon: <AdminPanelSettingsIcon />,
              })
            }
          >
            <ListItemIcon>
              <AdminPanelSettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>App administration</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Stack>
  );
};

export default UserAppShell;
